import {IssueClosedIcon, IssueOpenedIcon, type IconProps, SkipIcon} from '@primer/octicons-react'

import {IssueStateIcons} from './Icons'
import {
  categoryStatusVisual,
  type IssueStatusColor,
  type IssueStatusVisual,
  namedStatusVisual,
} from './issue-status-visuals'
import styles from './IssueStatusIcon.module.css'
import type {useIssueState} from './use-issue-pr-state'

export type IssueStatusIconStatus = {
  name?: string | null
  // Optional so a status without a category key, such as a board heading that lacks one, still gets
  // its named icon or the open or closed icon.
  category: {key?: string | null; state: 'OPEN' | 'CLOSED'}
}

export type IssueReferenceStatus = IssueStatusIconStatus & {
  name: string
  category: {stateReason?: string | null}
}

type IssueReferenceData = Partial<Pick<Parameters<typeof useIssueState>[0], 'state' | 'stateReason'>> & {
  status?: IssueReferenceStatus | null
}

// The legacy close/reopen buttons optimistically change only `state`/`stateReason`, so a cached
// status can briefly disagree with the lifecycle. Show it only while it still fits. References
// that select `stateReason` without `state` derive the lifecycle from the reason.
export function matchingIssueStatus({status, state, stateReason}: IssueReferenceData) {
  const lifecycleState = state ?? (stateReason === 'REOPENED' ? 'OPEN' : stateReason ? 'CLOSED' : undefined)
  return status &&
    (!lifecycleState || status.category.state === lifecycleState) &&
    (status.category.state === 'OPEN' || status.category.stateReason === (stateReason ?? 'COMPLETED'))
    ? status
    : undefined
}

const ICON_CLASSES: Record<IssueStatusColor, string> = {
  open: styles.open,
  done: styles.done,
  muted: styles.muted,
}

export function issueStatusVisual(status: IssueStatusIconStatus): IssueStatusVisual {
  return (
    namedStatusVisual(status.name) ??
    categoryStatusVisual(status.category.key) ??
    (status.category.state === 'OPEN' ? {icon: IssueOpenedIcon, color: 'open'} : {icon: IssueClosedIcon, color: 'done'})
  )
}

export function IssueStatusIcon({
  status,
  'aria-label': ariaLabel,
  size,
  className,
}: {
  status: IssueStatusIconStatus
  'aria-label'?: string
} & Pick<IconProps, 'size' | 'className'>) {
  const {icon: StatusIcon, color} = issueStatusVisual(status)
  return (
    <StatusIcon
      className={[className, ICON_CLASSES[color]].filter(Boolean).join(' ')}
      aria-label={ariaLabel}
      size={size}
    />
  )
}

type LifecycleIconProps = Pick<IconProps, 'size' | 'className' | 'aria-label'>

/** The issue's lifecycle icon, matching `useIssueState().sourceIcon('Issue')`. */
export function IssueLifecycleIcon({
  state,
  stateReason,
  ...props
}: Pick<IssueReferenceData, 'state' | 'stateReason'> & LifecycleIconProps) {
  if (!stateReason || stateReason === 'REOPENED') {
    return state === 'CLOSED' && !stateReason ? (
      <IssueStateIcons.COMPLETED {...props} />
    ) : (
      <IssueStateIcons.OPEN {...props} />
    )
  }
  switch (stateReason) {
    case 'COMPLETED':
      return <IssueStateIcons.COMPLETED {...props} />
    case 'NOT_PLANNED':
      return <IssueStateIcons.NOT_PLANNED {...props} />
    case 'DUPLICATE':
      return <IssueStateIcons.DUPLICATE {...props} />
    default:
      return <SkipIcon {...props} />
  }
}

export function IssueReferenceIcon({
  issue,
  ...props
}: {
  issue: IssueReferenceData
} & Pick<IconProps, 'size' | 'className'>) {
  const status = matchingIssueStatus(issue)

  if (status) return <IssueStatusIcon status={status} aria-label={status.name} {...props} />

  return <IssueLifecycleIcon state={issue.state} stateReason={issue.stateReason} {...props} />
}
